import React, { useState, useEffect, useRef } from 'react';

import {

    Button, TextField, Typography, Box, Dialog, DialogTitle, DialogContent,

    DialogContentText, DialogActions,

} from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';

import { toastSuccess, toastWarning } from '../../../../utils/utils';

import {

    getDQInsiderBrandingService,

    updateDQInsiderBrandingService,

} from '../../../../services/dq_insider.service';

import {

    getInsiderFeatureAccessService,

    setInsiderFeatureOverridesService,

} from '../../../../services/pac.service';

import { FEATURE_KEY } from '../../../../utils/constants';

import { toBrandingLogoSrc } from '../../../../utils/toBrandingLogoSrc';

import DefaultDQLogo from '../../../../assets/images/PoweredByDQ_Default.png';
import { isHttpUrl } from '../../../../utils/brandingAssets';



const DEFAULT_COLORS = {

    primary: '#FF6900',

    secondary: '#052F44',

    text: '#111827',

    background: '#FFFBF7',

    section_bg: '#FFFBF7',

    primary_title: '#111827',

    secondary_title: '#052F44',

    global_1: '#FF6900',

    global_2: '#7B8FC7',

    global_3: '#0097B2',

};

const COLOR_FIELDS = [
    { key: 'primary', label: 'Primary', help: 'Buttons, highlights and active states.' },
    { key: 'secondary', label: 'Secondary', help: 'Secondary buttons and accents.' },
    { key: 'text', label: 'Text', help: 'Default heading / body text color.' },
    { key: 'background', label: 'Background', help: 'App background color.' },
    { key: 'section_bg', label: 'Section background', help: 'Cards / section panels background.' },
    { key: 'primary_title', label: 'Primary title', help: 'Main heading color.' },
    { key: 'secondary_title', label: 'Secondary title', help: 'Sub-heading color.' },
    { key: 'global_1', label: 'Global color 1', help: 'Default: My Workspace button (#FF6900).' },
    { key: 'global_2', label: 'Global color 2', help: 'Default: Run an Assessment button (#7B8FC7).' },
    { key: 'global_3', label: 'Global color 3', help: 'Default: CPC Index button (#0097B2).' },
];

const CORE_COLOR_FIELDS = COLOR_FIELDS.slice(0, 7);
const EXTRA_COLOR_FIELDS = COLOR_FIELDS.slice(7);

const FONT_FIELDS = [
    { key: 'primary', label: 'Primary font', help: 'Upload a font file or paste a font URL.' },
    { key: 'secondary', label: 'Secondary font', help: 'Optional — upload a file or paste a URL.' },
];

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];

const ALLOWED_FONT_TYPES = [

    'font/woff', 'font/woff2', 'font/ttf', 'font/otf',

    'application/font-woff', 'application/font-woff2',

    'application/x-font-ttf', 'application/x-font-opentype', 'application/octet-stream',

];

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const MAX_FONT_BYTES = 2 * 1024 * 1024;

const MAX_CSS_BYTES = 50 * 1024;



const fileToDataUrl = (file) =>

    new Promise((resolve, reject) => {

        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);

        reader.onerror = reject;

        reader.readAsDataURL(file);

    });



const fileToBase64 = (file) =>

    new Promise((resolve, reject) => {

        const reader = new FileReader();

        reader.onload = () => {

            const result = String(reader.result || '');

            const idx = result.indexOf('base64,');

            resolve(idx >= 0 ? result.slice(idx + 7) : result);

        };

        reader.onerror = reject;

        reader.readAsDataURL(file);

    });



const emptyColors = COLOR_FIELDS.reduce((acc, f) => { acc[f.key] = ''; return acc; }, {});

const emptyFonts = { primary: '', secondary: '' };

const emptyFontUploads = { primary: null, secondary: null };



const BRANDING_MODES = [

    { value: 'off', label: 'Disabled', help: 'Default DQ look. Users do not see Settings → Appearance.' },

    { value: 'admin_only', label: 'Admin managed', help: 'Only staff edit here. Logo/colors apply for owner, peer and client.' },

    { value: 'self_service', label: 'User self-service', help: 'Owner, peer and client can edit in Settings → Appearance.' },

];



const deriveBrandingMode = (overrides = {}) => {

    const ovr = overrides[FEATURE_KEY.WHITE_LABEL_BRANDING];

    if (ovr === undefined || ovr === null) return 'off';

    if (!ovr.enabled) return 'off';

    return ovr?.config?.user_self_service ? 'self_service' : 'admin_only';

};



const buildModeOverride = (mode) => {

    const key = FEATURE_KEY.WHITE_LABEL_BRANDING;

    if (mode === 'off') return { [key]: { enabled: false } };

    return {

        [key]: {

            enabled: true,

            config: { user_self_service: mode === 'self_service' },

        },

    };

};

const cardStyle = {
    backgroundColor: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: 14,
    padding: 24,
    marginBottom: 16,
};

const sectionTitleStyle = { fontSize: '1.05rem', fontWeight: 700, color: '#111827', marginBottom: 4 };
const sectionHintStyle = { fontSize: '0.82rem', color: '#6B7280', marginBottom: 16 };

const ColorGrid = ({ fields, colors, onColorChange }) => (
    <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', columnGap: 24, rowGap: 20 }}>
        {fields.map(({ key, label, help }) => {
            const v = colors[key];
            const swatch = (v && HEX.test(String(v).trim())) ? v.trim() : DEFAULT_COLORS[key];
            return (
                <Box key={key}>
                    <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', marginBottom: 6 }}>{label}</Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                            type="color"
                            value={swatch}
                            onChange={(e) => onColorChange(key, e.target.value)}
                            aria-label={`${label} color`}
                            style={{ width: 44, height: 40, border: '1px solid #E5E7EB', borderRadius: 8, padding: 2, background: '#fff', cursor: 'pointer' }}
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder={DEFAULT_COLORS[key]}
                            value={v}
                            onChange={(e) => onColorChange(key, e.target.value)}
                            style={{ width: 140 }}
                        />
                        {v ? (
                            <Button size="small" onClick={() => onColorChange(key, '')} style={{ color: '#6B7280', textTransform: 'none' }}>Default</Button>
                        ) : null}
                    </Box>
                    {help ? (
                        <Typography style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 4 }}>{help}</Typography>
                    ) : null}
                </Box>
            );
        })}
    </Box>
);



const BrandingPanel = ({ active, dqInsiderId, refreshKey = 0, onAccessModeChanged }) => {

    const fileInputRef = useRef(null);

    const cssFileInputRef = useRef(null);

    const fontFileInputRefs = useRef({ primary: null, secondary: null });

    const [loading, setLoading] = useState(false);

    const [savingColors, setSavingColors] = useState(false);

    const [uploadingLogo, setUploadingLogo] = useState(false);

    const [removingLogo, setRemovingLogo] = useState(false);

    const [logoUrl, setLogoUrl] = useState(null);

    const [logoLoadFailed, setLogoLoadFailed] = useState(false);

    const [colors, setColors] = useState(emptyColors);

    const [fontLinks, setFontLinks] = useState(emptyFonts);

    const [fontUploads, setFontUploads] = useState(emptyFontUploads);

    const [stylesheetLink, setStylesheetLink] = useState('');

    const [stylesheetUpload, setStylesheetUpload] = useState(null);

    const [stylesheetPreview, setStylesheetPreview] = useState('');

    const [accessMode, setAccessMode] = useState('off');

    const [savingMode, setSavingMode] = useState(false);

    const [resetDialogOpen, setResetDialogOpen] = useState(false);



    const loadStylesheetPreview = async (url) => {

        if (!url) { setStylesheetPreview(''); return; }

        try {

            const res = await fetch(url);

            if (res.ok) setStylesheetPreview(await res.text());

            else setStylesheetPreview('(Could not load preview)');

        } catch {

            setStylesheetPreview('(Could not load preview)');

        }

    };



    useEffect(() => {

        if (!active || !dqInsiderId) return;

        let cancelled = false;

        (async () => {

            setLoading(true);

            try {

                const [brandingRes, pacRes] = await Promise.all([

                    getDQInsiderBrandingService(dqInsiderId),

                    getInsiderFeatureAccessService(dqInsiderId),

                ]);

                if (cancelled) return;

                const b = (brandingRes && brandingRes.data && brandingRes.data.branding) || {};

                const c = b.colors || {};

                const overrides = (pacRes && pacRes.data && pacRes.data.overrides) || {};

                setAccessMode(deriveBrandingMode(overrides));

                const f = b.fonts || {};

                setLogoUrl(b.logo_url || null);

                setLogoLoadFailed(false);

                setColors(COLOR_FIELDS.reduce((acc, fld) => { acc[fld.key] = c[fld.key] || ''; return acc; }, {}));

                setFontLinks({ primary: f.primary || '', secondary: f.secondary || '' });

                setFontUploads(emptyFontUploads);

                const sheet = b.custom_stylesheet || '';

                setStylesheetLink(sheet);

                setStylesheetUpload(null);

                loadStylesheetPreview(sheet);

            } catch {

                /* invokeApi toasted */

            } finally {

                if (!cancelled) setLoading(false);

            }

        })();

        return () => { cancelled = true; };

    }, [active, dqInsiderId, refreshKey]);



    useEffect(() => { setLogoLoadFailed(false); }, [logoUrl]);



    const handleColorChange = (key, value) => setColors((prev) => ({ ...prev, [key]: value }));



    const handleFontLinkChange = (key, value) => {

        setFontLinks((prev) => ({ ...prev, [key]: value }));

        setFontUploads((prev) => ({ ...prev, [key]: null }));

    };



    const handleFontFile = async (key, e) => {

        const file = e.target.files && e.target.files[0];

        if (fontFileInputRefs.current[key]) fontFileInputRefs.current[key].value = '';

        if (!file) return;

        if (!ALLOWED_FONT_TYPES.includes(file.type) && !/\.(woff2?|ttf|otf)$/i.test(file.name)) {

            toastWarning('Use a WOFF, WOFF2, TTF or OTF font file.');

            return;

        }

        if (file.size > MAX_FONT_BYTES) { toastWarning('Font file must be under 2 MB.'); return; }

        const file_base64 = await fileToBase64(file);

        setFontUploads((prev) => ({ ...prev, [key]: { file_base64, mime_type: file.type || 'application/octet-stream', name: file.name } }));

        setFontLinks((prev) => ({ ...prev, [key]: '' }));

    };



    const handleCssFile = async (e) => {

        const file = e.target.files && e.target.files[0];

        if (cssFileInputRef.current) cssFileInputRef.current.value = '';

        if (!file) return;

        if (file.size > MAX_CSS_BYTES) { toastWarning('CSS file must be under 50 KB.'); return; }

        const file_base64 = await fileToBase64(file);

        setStylesheetUpload({ file_base64 });

        setStylesheetLink('');

        setStylesheetPreview(await file.text());

    };



    const handleModeChange = async (mode) => {

        if (!dqInsiderId || mode === accessMode) return;

        setSavingMode(true);

        try {

            await setInsiderFeatureOverridesService(dqInsiderId, buildModeOverride(mode));

            setAccessMode(mode);

            toastSuccess('Branding access updated.');

            if (typeof onAccessModeChanged === 'function') onAccessModeChanged();

        } catch {

            /* toasted upstream */

        } finally {

            setSavingMode(false);

        }

    };



    const brandingEnabled = accessMode !== 'off';



    const handleSaveColors = async () => {

        const payloadColors = {};

        for (const { key, label } of COLOR_FIELDS) {

            const v = String(colors[key] || '').trim();

            if (!v) { payloadColors[key] = null; continue; }

            if (!HEX.test(v)) { toastWarning(`Invalid ${label} color. Use a hex value like #1A2B3C.`); return; }

            payloadColors[key] = v;

        }

        const payloadFonts = {};

        const payloadFontUploads = {};

        for (const { key } of FONT_FIELDS) {

            if (fontUploads[key]) {

                payloadFontUploads[key] = {

                    file_base64: fontUploads[key].file_base64,

                    mime_type: fontUploads[key].mime_type,

                };

            } else {

                const link = String(fontLinks[key] || '').trim();

                if (link && !isHttpUrl(link)) {

                    toastWarning('Font link must be a valid URL (https://…).');

                    return;

                }

                payloadFonts[key] = link || null;

            }

        }

        const payload = {

            colors: payloadColors,

            fonts: Object.keys(payloadFonts).length ? payloadFonts : undefined,

            font_uploads: Object.keys(payloadFontUploads).length ? payloadFontUploads : undefined,

        };

        if (stylesheetUpload) payload.stylesheet_upload = stylesheetUpload;

        else {

            const link = String(stylesheetLink || '').trim();

            if (link && !isHttpUrl(link)) {

                toastWarning('Stylesheet link must be a valid URL (https://…).');

                return;

            }

            payload.custom_stylesheet = link || null;

        }



        setSavingColors(true);

        try {

            const res = await updateDQInsiderBrandingService(dqInsiderId, payload);

            if (res && res.data && res.data.branding) {

                const b = res.data.branding;

                const c = b.colors || {};

                const f = b.fonts || {};

                setColors(COLOR_FIELDS.reduce((acc, fld) => { acc[fld.key] = c[fld.key] || ''; return acc; }, {}));

                setFontLinks({ primary: f.primary || '', secondary: f.secondary || '' });

                setFontUploads(emptyFontUploads);

                const sheet = b.custom_stylesheet || '';

                setStylesheetLink(sheet);

                setStylesheetUpload(null);

                loadStylesheetPreview(sheet);

            }

            toastSuccess('Branding saved.');

        } catch {

            /* toasted upstream */

        } finally {

            setSavingColors(false);

        }

    };



    const handleResetConfirm = async () => {

        setResetDialogOpen(false);

        setSavingColors(true);

        try {

            const resetColors = COLOR_FIELDS.reduce((acc, f) => { acc[f.key] = null; return acc; }, {});

            await updateDQInsiderBrandingService(dqInsiderId, {

                colors: resetColors,

                fonts: { primary: null, secondary: null },

                custom_stylesheet: null,

            });

            setColors(emptyColors);

            setFontLinks(emptyFonts);

            setFontUploads(emptyFontUploads);

            setStylesheetLink('');

            setStylesheetPreview('');

            toastSuccess('Branding reset to default.');

        } catch {

            /* toasted upstream */

        } finally {

            setSavingColors(false);

        }

    };



    const handleLogoPick = async (e) => {

        const file = e.target.files && e.target.files[0];

        if (fileInputRef.current) fileInputRef.current.value = '';

        if (!file) return;

        if (!ALLOWED_LOGO_TYPES.includes(file.type)) { toastWarning('Use a PNG, JPG, SVG or WEBP image.'); return; }

        if (file.size > MAX_LOGO_BYTES) { toastWarning('Logo must be under 2 MB.'); return; }

        setUploadingLogo(true);

        try {

            const dataUrl = await fileToDataUrl(file);

            setLogoLoadFailed(false);

            const res = await updateDQInsiderBrandingService(dqInsiderId, { image_base64: dataUrl, mime_type: file.type });

            if (res && res.data && res.data.branding && res.data.branding.logo_url) {

                setLogoUrl(res.data.branding.logo_url);

                toastSuccess('Logo updated.');

            }

        } catch {

            /* toasted upstream */

        } finally {

            setUploadingLogo(false);

        }

    };



    const handleRemoveLogo = async () => {

        setRemovingLogo(true);

        try {

            await updateDQInsiderBrandingService(dqInsiderId, { logo_url: null });

            setLogoUrl(null);

            setLogoLoadFailed(false);

            toastSuccess('Logo removed. Default logo restored.');

        } catch {

            /* toasted upstream */

        } finally {

            setRemovingLogo(false);

        }

    };



    if (loading) {

        return (

            <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>

                <CircularProgress size={26} />

            </Box>

        );

    }



    return (

        <Box>

            <Typography style={{ fontWeight: 700, marginBottom: 4 }}>Branding access</Typography>

            <Typography style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>

                Workspaces with existing branding keep working until you pick a mode here.

            </Typography>

            {BRANDING_MODES.map(({ value, label, help }) => (

                <Box key={value} style={{ marginBottom: 10 }}>

                    <Button

                        variant={accessMode === value ? 'contained' : 'outlined'}

                        color="primary"

                        size="small"

                        disabled={savingMode}

                        onClick={() => handleModeChange(value)}

                        style={{ marginRight: 8, textTransform: 'none' }}

                    >

                        {label}

                    </Button>

                    <Typography component="span" style={{ fontSize: 12, color: '#6B7280' }}>{help}</Typography>

                </Box>

            ))}



            {!brandingEnabled ? (

                <Typography style={{ fontSize: 13, color: '#9CA3AF', marginTop: 16 }}>

                    Select Admin managed or User self-service to configure logo and colors.

                </Typography>

            ) : (

            <Box style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Logo card */}
            <Box style={cardStyle}>
            <Typography style={sectionTitleStyle}>Workspace logo</Typography>
            <Typography style={sectionHintStyle}>
                Shown in the sidebar across this workspace. Transparent PNG or SVG works best.
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>

                <Box style={{ height: 72, minWidth: 180, padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #D1D5DB', borderRadius: 10, background: '#FAFAFA' }}>

                    {logoUrl && !logoLoadFailed ? (

                        <img src={toBrandingLogoSrc(logoUrl)} alt="" role="presentation"

                             onError={() => setLogoLoadFailed(true)}

                             style={{ height: 48, width: 'auto', maxWidth: 240, objectFit: 'contain' }} />

                    ) : (

                        <img src={DefaultDQLogo} alt="" role="presentation"

                             style={{ height: 48, width: 'auto', maxWidth: 240, objectFit: 'contain' }} />

                    )}

                </Box>

                <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>

                    <Button variant="contained" color="primary" disabled={uploadingLogo}

                            onClick={() => fileInputRef.current && fileInputRef.current.click()}>

                        {uploadingLogo ? 'Uploading…' : (logoUrl ? 'Replace logo' : 'Upload logo')}

                    </Button>

                    {logoUrl && (

                        <Button variant="outlined" disabled={removingLogo} onClick={handleRemoveLogo}>

                            {removingLogo ? 'Removing…' : 'Remove'}

                        </Button>

                    )}

                    <input ref={fileInputRef} type="file" style={{ display: 'none' }}

                           accept=".png,.jpg,.jpeg,.svg,.webp,image/*" onChange={handleLogoPick} />

                </Box>

            </Box>
            </Box>



            {/* Brand colors card */}
            <Box style={cardStyle}>
            <Typography style={sectionTitleStyle}>Brand colors</Typography>
            <Typography style={{ ...sectionHintStyle, marginBottom: 20 }}>
                Applied across the workspace. Leave empty to use the DQ default.
            </Typography>
            <ColorGrid fields={CORE_COLOR_FIELDS} colors={colors} onColorChange={handleColorChange} />
            </Box>



            {/* Extra colors card */}
            <Box style={cardStyle}>
            <Typography style={sectionTitleStyle}>Extra colors</Typography>
            <Typography style={{ ...sectionHintStyle, marginBottom: 20 }}>
                Used by dashboard buttons and custom styles (global_1–3 CSS variables).
            </Typography>
            <ColorGrid fields={EXTRA_COLOR_FIELDS} colors={colors} onColorChange={handleColorChange} />
            </Box>



            {/* Fonts card */}
            <Box style={cardStyle}>
            <Typography style={sectionTitleStyle}>Fonts</Typography>
            <Typography style={sectionHintStyle}>
                Upload a font file or paste a font URL.
            </Typography>

            {FONT_FIELDS.map(({ key, label, help }) => (

                <Box key={key} style={{ marginBottom: 16 }}>

                    <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', marginBottom: 6 }}>{label}</Typography>

                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>

                        <TextField

                            size="small"

                            variant="outlined"

                            style={{ flex: 1, minWidth: 180, maxWidth: 420 }}

                            placeholder="https://…"

                            disabled={!!fontUploads[key]}

                            value={fontUploads[key] ? fontUploads[key].name : fontLinks[key]}

                            onChange={(e) => handleFontLinkChange(key, e.target.value)}

                        />

                        <Button

                            size="small"

                            variant="outlined"

                            style={{ flexShrink: 0, textTransform: 'none' }}

                            onClick={() => fontFileInputRefs.current[key] && fontFileInputRefs.current[key].click()}

                        >

                            Upload file

                        </Button>

                        {(fontLinks[key] || fontUploads[key]) && (

                            <Button

                                size="small"

                                style={{ flexShrink: 0, textTransform: 'none' }}

                                onClick={() => {

                                    setFontLinks((p) => ({ ...p, [key]: '' }));

                                    setFontUploads((p) => ({ ...p, [key]: null }));

                                }}

                            >

                                Clear

                            </Button>

                        )}

                        <input

                            ref={(el) => { fontFileInputRefs.current[key] = el; }}

                            type="file"

                            style={{ display: 'none' }}

                            accept=".woff,.woff2,.ttf,.otf,font/*"

                            onChange={(e) => handleFontFile(key, e)}

                        />

                    </Box>

                    {fontUploads[key] && (

                        <Typography style={{ fontSize: '0.75rem', color: '#059669', marginTop: 4 }}>

                            File attached — click Save branding to upload.

                        </Typography>

                    )}

                    {help ? (
                        <Typography style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 4 }}>{help}</Typography>
                    ) : null}

                </Box>

            ))}
            </Box>



            {/* Custom CSS card */}
            <Box style={cardStyle}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>

                <Typography style={{ ...sectionTitleStyle, marginBottom: 0 }}>Custom CSS</Typography>

                <Button size="small" variant="outlined" style={{ textTransform: 'none' }} onClick={() => cssFileInputRef.current && cssFileInputRef.current.click()}>

                    Upload .css file

                </Button>

                <input ref={cssFileInputRef} type="file" accept=".css,text/css" style={{ display: 'none' }} onChange={handleCssFile} />

            </Box>

            <Typography style={{ ...sectionHintStyle, marginTop: 8 }}>
                Upload a .css file or paste a stylesheet URL. Preview only — edit the file and re-upload to change.
            </Typography>

            <TextField size="small" fullWidth variant="outlined" placeholder="https://…/custom.css"

                       value={stylesheetLink} onChange={(e) => { setStylesheetLink(e.target.value); setStylesheetUpload(null); }}

                       style={{ marginBottom: 12, maxWidth: 520 }} />

            <Typography style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Preview</Typography>

            <Box style={{ padding: 16, border: '1px solid #E5E7EB', borderRadius: 8, background: '#F9FAFB', maxHeight: 240, overflow: 'auto' }}>

                <Typography component="pre" style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>

                    {stylesheetPreview || '(No stylesheet)'}

                </Typography>

            </Box>
            </Box>



            <Box style={{ display: 'flex', gap: 8, marginTop: 4 }}>

                <Button onClick={handleSaveColors} variant="contained" color="primary" disabled={savingColors}

                        startIcon={savingColors ? <CircularProgress size={15} style={{ color: '#fff' }} /> : null}>

                    {savingColors ? 'Saving…' : 'Save branding'}

                </Button>

                <Button onClick={() => setResetDialogOpen(true)} disabled={savingColors} style={{ color: '#6B7280', textTransform: 'none' }}>

                    Reset to default

                </Button>

            </Box>



            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>

                <DialogTitle>Reset to default?</DialogTitle>

                <DialogContent>

                    <DialogContentText>

                        This will remove all custom branding for this workspace and restore the default DQ appearance.

                    </DialogContentText>

                </DialogContent>

                <DialogActions>

                    <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>

                    <Button onClick={handleResetConfirm} color="secondary" variant="contained">Reset</Button>

                </DialogActions>

            </Dialog>

            </Box>

            )}

        </Box>

    );

};



export default BrandingPanel;


